import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { V4Pool } from '../types';
import { apiService } from '../services/api';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalBox = styled.div`
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 24px 12px;
  border-bottom: 1px solid var(--border-color);
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1.125rem;
  color: var(--text-primary);
`;

const Subtitle = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 4px;
  font-family: 'SF Mono', Monaco, monospace;
`;

const CloseBtn = styled.button`
  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  width: 32px;
  height: 32px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  &:hover { background: var(--bg-tertiary); }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 24px;
  align-items: center;
  border-bottom: 1px solid var(--border-color);
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 0.875rem;
`;

const SortSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 24px 16px;
`;

const StateMessage = styled.div`
  text-align: center;
  color: var(--text-muted);
  padding: 40px 0;
  font-size: 0.9375rem;
`;

const PoolCard = styled.div<{ selected?: boolean; warn?: boolean }>`
  border: 1px solid ${p => p.selected ? 'var(--accent-primary)' : 'var(--border-color)'};
  border-left: 3px solid ${p =>
    p.selected ? 'var(--accent-primary)' :
    p.warn ? '#f59e0b' : 'var(--border-color)'
  };
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
  background: ${p => p.selected ? 'var(--bg-secondary)' : 'var(--bg-primary)'};
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const PoolHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const PoolIdText = styled.div`
  font-family: 'SF Mono', Monaco, monospace;
  font-size: 0.8125rem;
  color: var(--text-primary);
  font-weight: 600;
`;

const PoolStats = styled.div`
  font-size: 0.8125rem;
  color: var(--text-secondary);
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
`;

const Badge = styled.span<{ kind?: 'warn' | 'danger' | 'info' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.6875rem;
  font-weight: 600;
  ${p => p.kind === 'warn' && `background: rgba(245, 158, 11, 0.15); color: #f59e0b;`}
  ${p => p.kind === 'danger' && `background: rgba(239, 68, 68, 0.15); color: #ef4444;`}
  ${p => (p.kind === 'info' || !p.kind) && `background: rgba(59, 130, 246, 0.15); color: #3b82f6;`}
`;

const SelectBtn = styled.button`
  background: var(--accent-primary);
  color: white;
  border: none;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: var(--accent-hover); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

interface PoolSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  tokenAddress: string;
  quoteTokenAddress: string;
  onSelect: (pool: V4Pool) => void;
  currentlySelectedPoolId?: string;
  dexType: 'uniswap-v3' | 'uniswap-v4';
}

const truncate = (s: string, head = 10, tail = 6): string =>
  s.length > head + tail + 2 ? `${s.slice(0, head)}…${s.slice(-tail)}` : s;

const formatFee = (pool: V4Pool): string => {
  if (pool.is_dynamic_fee) return 'DYNAMIC';
  return `${(pool.fee_pips / 10000).toFixed(2)}%`;
};

const formatTvl = (v: number | null): string => {
  if (v == null) return '—';
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
};

const PoolSelectorModal: React.FC<PoolSelectorModalProps> = ({
  isOpen,
  onClose,
  tokenAddress,
  quoteTokenAddress,
  onSelect,
  currentlySelectedPoolId,
  dexType,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pools, setPools] = useState<V4Pool[]>([]);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'tvl' | 'volume24h'>('tvl');

  const fetchPools = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await apiService.discoverV4Pools(
        tokenAddress,
        quoteTokenAddress,
        'base',
        dexType,
      );
      // Защита от malformed response (backend на старом коде или 404/HTML вместо JSON)
      const list = Array.isArray(resp?.pools) ? resp.pools : [];
      setPools(list);
      if (!resp?.pools) {
        setError('Backend response missing "pools" field. Maybe /pools/discover endpoint not deployed yet?');
      }
    } catch (err: any) {
      setPools([]);
      setError(err?.response?.data?.detail || err?.message || 'Discovery failed');
    } finally {
      setLoading(false);
    }
  }, [tokenAddress, quoteTokenAddress, dexType]);

  useEffect(() => {
    if (!isOpen) return;
    fetchPools();
  }, [isOpen, fetchPools]);

  // ESC закрытие
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const filtered = (pools || []).filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return p.pool_id.toLowerCase().includes(q)
      || (p.hooks || '').toLowerCase().includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'tvl') {
      return (b.liquidity_usd || 0) - (a.liquidity_usd || 0);
    }
    return (b.volume_24h_usd || 0) - (a.volume_24h_usd || 0);
  });

  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <Header>
          <div>
            <Title>🏊 Select {dexType === 'uniswap-v4' ? 'Uniswap V4' : 'Uniswap V3'} Pool</Title>
            <Subtitle>
              Token: {truncate(tokenAddress)} ↔ Quote: {truncate(quoteTokenAddress)}
            </Subtitle>
          </div>
          <CloseBtn type="button" onClick={onClose}>✕</CloseBtn>
        </Header>

        <Toolbar>
          <SearchInput
            type="text"
            placeholder="Search by pool_id or hook…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <SortSelect value={sortBy} onChange={(e) => setSortBy(e.target.value as any)}>
            <option value="tvl">Sort: TVL</option>
            <option value="volume24h">Sort: Volume 24h</option>
          </SortSelect>
        </Toolbar>

        <Body>
          {loading && <StateMessage>⟳ Discovering pools…</StateMessage>}
          {!loading && error && (
            <StateMessage>
              ⚠ {error}
              <div style={{ marginTop: 12 }}>
                <SelectBtn type="button" onClick={fetchPools}>Retry</SelectBtn>
              </div>
            </StateMessage>
          )}
          {!loading && !error && sorted.length === 0 && (
            <StateMessage>
              No {dexType === 'uniswap-v4' ? 'V4' : 'V3'} pools found for this token pair on Base.<br />
              Tip: try a different quote token or switch to KyberSwap.
            </StateMessage>
          )}
          {!loading && !error && sorted.map((pool) => {
            const selected = currentlySelectedPoolId?.toLowerCase() === pool.pool_id.toLowerCase();
            const warn = pool.has_hook || pool.is_dynamic_fee;
            return (
              <PoolCard key={pool.pool_id} selected={selected} warn={warn}>
                <PoolHeader>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <PoolIdText>{truncate(pool.pool_id)}</PoolIdText>
                    <PoolStats>
                      <span>TVL <strong>{formatTvl(pool.liquidity_usd)}</strong></span>
                      <span>Vol 24h <strong>{formatTvl(pool.volume_24h_usd)}</strong></span>
                      {pool.price_usd != null && <span>Price <strong>${pool.price_usd.toFixed(6)}</strong></span>}
                      <span>Fee <strong>{formatFee(pool)}</strong></span>
                      {pool.tick_spacing != null && <span>Tick {pool.tick_spacing}</span>}
                    </PoolStats>
                  </div>
                  <SelectBtn type="button" onClick={() => { onSelect(pool); onClose(); }} disabled={selected}>
                    {selected ? '✓ Selected' : 'Select'}
                  </SelectBtn>
                </PoolHeader>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {pool.is_dynamic_fee && <Badge kind="danger">⚠ DYNAMIC FEE</Badge>}
                  {pool.has_hook && !pool.is_dynamic_fee && (
                    <Badge kind="warn">⚠ has hook {truncate(pool.hooks || '', 6, 4)}</Badge>
                  )}
                  <Badge>{pool.dex_type}</Badge>
                </div>
              </PoolCard>
            );
          })}
        </Body>
      </ModalBox>
    </Overlay>
  );
};

export default PoolSelectorModal;
